import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Live MapLibre camera captured at onboarding entry. Center/zoom/bearing/pitch live
// on the map instance, not Redux, so they ride along in the snapshot to be
// restored on exit. Null when no map is mounted.
export type MapCamera = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
};

// Stored as unknown so we don't pull the deeply-nested slice types into the
// onboarding slice — TS gives up on instantiation depth otherwise.
//
// Snapshot covers every slice the onboarding can mutate: dataset/map/ui plus the
// analysis-config slices (spatial, analysisSummary, globalFilter, widget).
// `analysisJob` is intentionally excluded — it tracks real BE job state.
// `mapCamera` is the imperative map-instance view, restored alongside the slices.
// `entryPath` is the route the user entered onboarding from, navigated back to on
// exit. Null when absent (e.g. a snapshot persisted before this field existed) —
// treated as no-redirect.
export type OnboardingSnapshot = {
  dataset: unknown;
  map: unknown;
  ui: unknown;
  spatial: unknown;
  analysisSummary: unknown;
  globalFilter: unknown;
  widget: unknown;
  mapCamera: MapCamera | null;
  entryPath: string | null;
};

export interface OnboardingState {
  active: boolean;
  moduleCode: string | null;
  snapshot: OnboardingSnapshot | null;
  // tourId of the step currently shown. Lifted out of the tour's local step
  // state so components outside the tour (e.g. sidebar nav items) can react to
  // the active step — used to force a nav item into its hover state while its
  // step is on screen. Null when no step is active.
  activeStepTourId: string | null;
}

export const onboardingInitialState: OnboardingState = {
  active: false,
  moduleCode: null,
  snapshot: null,
  activeStepTourId: null
};

export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState: onboardingInitialState,
  reducers: {
    enterOnboardingMode: (
      state,
      action: PayloadAction<{
        moduleCode: string;
        snapshot: OnboardingSnapshot;
      }>
    ) => {
      state.active = true;
      state.moduleCode = action.payload.moduleCode;
      state.snapshot = action.payload.snapshot;
      // Cleared on enter; useTourStep sets it to the resumed/first step on mount.
    },
    exitOnboardingMode: (state) => {
      state.active = false;
      state.moduleCode = null;
      state.snapshot = null;
      state.activeStepTourId = null;
    },
    setActiveStepTourId: (state, action: PayloadAction<string | null>) => {
      state.activeStepTourId = action.payload;
    }
  }
});

export const { enterOnboardingMode, exitOnboardingMode, setActiveStepTourId } =
  onboardingSlice.actions;
export const onboardingReducer = onboardingSlice.reducer;
